import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Upload, ScanLine, Loader2, CheckCircle2, X, Download,
  FileText, Landmark, AlertTriangle, RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Income', 'Transfer', 'Rent/Mortgage', 'Utilities', 'Insurance',
  'Groceries', 'Dining', 'Transportation', 'Shopping', 'Healthcare',
  'Entertainment', 'Subscriptions', 'Payroll', 'Taxes', 'Loan Payment',
  'Credit Card Payment', 'ATM Withdrawal', 'Fee', 'Other'
];

export default function DocumentScanner({ workspaceId, docType = 'bank_statement', onImportSuccess }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [editedTransactions, setEditedTransactions] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const handleFile = (f) => {
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|png|jpg|jpeg)$/i)) {
      toast.error('Please upload a PDF, PNG, or JPG file.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum 20MB.');
      return;
    }
    setFile(f);
    setJob(null);
    setEditedTransactions([]);
    setImportResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFile(e.dataTransfer.files[0]);
  };

  // Poll for job completion
  const pollJob = useCallback(async (jobId) => {
    try {
      const res = await apiFetch(`${API_URL}/api/ocr/scan-job/${jobId}`);
      if (!res.ok) return;
      const data = await res.json();
      setJob(data);

      const key = docType === 'bank_statement' ? 'transactions' : 'line_items';
      const items = data[key] || [];
      setEditedTransactions(items.map((t, i) => ({ ...t, _id: i, _selected: true })));

      if (data.status === 'complete' || data.status === 'partial_error') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data.status === 'complete') {
          toast.success(`All ${data.pages_total} pages processed — ${items.length} items extracted`);
        } else {
          toast.warning('Some pages could not be processed. Partial results are shown.');
        }
      }
    } catch {
      // silent — will retry on next poll
    }
  }, [docType]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleExtract = async () => {
    if (!file) return;
    setUploading(true);
    setJob(null);
    setImportResult(null);
    setEditedTransactions([]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspace_id', workspaceId);
      formData.append('doc_type', docType);

      const response = await apiFetch(`${API_URL}/api/ocr/scan-job`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setJob(data);
        toast.success(`Processing ${data.pages_total} page(s) — results will appear as each page completes`);

        // Start polling immediately (all processing is async now)
        pollRef.current = setInterval(() => pollJob(data.job_id), 4000);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed. Please try again.');
      }
    } catch {
      toast.error('Upload failed. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const toggleTransaction = (idx) => {
    setEditedTransactions(prev => prev.map((t, i) => i === idx ? { ...t, _selected: !t._selected } : t));
  };

  const updateTransaction = (idx, field, value) => {
    setEditedTransactions(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleImport = async () => {
    const selected = editedTransactions.filter(t => t._selected);
    if (selected.length === 0) { toast.warning('No transactions selected.'); return; }
    if (!job) return;

    setImporting(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ocr/scan-job/${job.job_id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transactions: selected.map(({ _id, _selected, ...t }) => t),
          account_info: job.account_info || {},
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
        toast.success(data.message);
        if (onImportSuccess) onImportSuccess(data);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Import failed');
      }
    } catch {
      toast.error('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setFile(null);
    setJob(null);
    setEditedTransactions([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectedCount = editedTransactions.filter(t => t._selected).length;
  const isProcessing = job?.status === 'processing';

  // ─── IMPORT SUCCESS ───────────────────────────────────────────────
  if (importResult) {
    return (
      <div className="space-y-4" data-testid="scanner-import-success">
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <div>
              <p className="text-xl font-semibold text-foreground">
                {importResult.imported_count} Transactions Imported
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                From {job?.filename || 'document'}
              </p>
            </div>
            <Button onClick={handleReset} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="scan-another-after-import">
              <ScanLine className="w-4 h-4" /> Scan Another Document
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── UPLOAD AREA ──────────────────────────────────────────────────
  if (!job) {
    return (
      <div className="space-y-4" data-testid="scanner-upload">
        <Card className="border-2 border-dashed border-border bg-muted/50">
          <CardContent className="p-8">
            <div
              className={cn(
                "flex flex-col items-center justify-center gap-4 py-8 rounded-xl transition-colors cursor-pointer",
                dragOver && "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400"
              )}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} data-testid="file-input" />

              {uploading ? (
                <>
                  <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                  <div className="text-center">
                    <p className="text-base font-semibold text-foreground">
                      Analyzing document with AI...
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Processing first pages — this may take 15-30 seconds
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    {docType === 'bank_statement'
                      ? <Landmark className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                      : <FileText className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />}
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-foreground">
                      {file ? file.name : `Drop your ${docType === 'bank_statement' ? 'bank statement' : 'invoice'} here`}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {file
                        ? `${(file.size / 1024).toFixed(1)} KB — Ready to extract`
                        : 'PDF, PNG, or JPG up to 20MB'}
                    </p>
                  </div>
                  {file && (
                    <div className="flex gap-3 mt-2">
                      <Button onClick={(e) => { e.stopPropagation(); handleExtract(); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="extract-btn">
                        <ScanLine className="w-4 h-4" /> Extract Data
                      </Button>
                      <Button variant="outline" onClick={(e) => { e.stopPropagation(); handleReset(); }} data-testid="reset-btn">
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── PROCESSING STATE (job created, waiting for first results) ──────
  if (job && editedTransactions.length === 0 && isProcessing) {
    return (
      <div className="space-y-4" data-testid="scanner-processing">
        <Card>
          <CardContent className="p-10 text-center space-y-5">
            <Loader2 className="w-14 h-14 text-emerald-500 animate-spin mx-auto" />
            <div>
              <p className="text-lg font-semibold text-foreground">
                Analyzing your document with AI...
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Processing page {job.pages_processed || 0} of {job.pages_total} — {job.filename}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Each page takes 15-30 seconds. Transactions will appear as they're extracted.
              </p>
            </div>
            {/* Progress bar */}
            <div className="w-64 mx-auto">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.max(5, ((job.pages_processed || 0) / (job.pages_total || 1)) * 100)}%` }} />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleReset} className="mt-2" data-testid="cancel-scan-btn">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── ERROR STATE ──────────────────────────────────────────────────
  if (job && (job.status === 'failed')) {
    return (
      <div className="space-y-4" data-testid="scanner-error">
        <Card className="border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-900/10">
          <CardContent className="p-8 text-center space-y-4">
            <AlertTriangle className="w-14 h-14 text-rose-500 mx-auto" />
            <div>
              <p className="text-lg font-semibold text-foreground">
                Extraction Failed
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {job.error || 'An unexpected error occurred. Please try again.'}
              </p>
            </div>
            <Button onClick={handleReset} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="retry-scan-btn">
              <RefreshCw className="w-4 h-4" /> Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── BANK STATEMENT RESULTS ───────────────────────────────────────
  if (docType === 'bank_statement') {
    return (
      <div className="space-y-4" data-testid="scanner-results">
        {/* Progress / Summary Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                <div>
                  <p className="font-semibold text-foreground">
                    {editedTransactions.length} Transactions Extracted
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {job.account_info?.bank_name} {job.account_info?.account_last4 ? `····${job.account_info.account_last4}` : ''}
                    {' — '}{job.filename}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {isProcessing && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 dark:border-amber-700 gap-1 animate-pulse" data-testid="processing-badge">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Processing page {job.pages_processed}/{job.pages_total}...
                  </Badge>
                )}
                {job.status === 'partial_error' && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1">
                    <AlertTriangle className="w-3 h-3" /> Partial results
                  </Badge>
                )}
                <Badge variant="outline" className="text-emerald-600">{selectedCount} selected</Badge>
                <Button size="sm" onClick={handleImport} disabled={importing || selectedCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="import-btn">
                  {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  Import {selectedCount}
                </Button>
                <Button size="sm" variant="outline" onClick={handleReset} data-testid="scan-another-btn">
                  <ScanLine className="w-3 h-3 mr-1" /> Scan Another
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b border-border bg-muted">
                    <th className="p-3 text-left w-10">
                      <input type="checkbox" checked={selectedCount === editedTransactions.length && editedTransactions.length > 0}
                        onChange={() => {
                          const allSelected = selectedCount === editedTransactions.length;
                          setEditedTransactions(prev => prev.map(t => ({ ...t, _selected: !allSelected })));
                        }} className="rounded" />
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Date</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Description</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Amount</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Type</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {editedTransactions.map((txn, idx) => (
                    <tr key={idx} className={cn(
                      "border-b border-border transition-colors",
                      txn._selected ? "bg-card" : "bg-muted/50 opacity-60"
                    )} data-testid={`txn-row-${idx}`}>
                      <td className="p-3">
                        <input type="checkbox" checked={txn._selected} onChange={() => toggleTransaction(idx)} className="rounded" />
                      </td>
                      <td className="p-3">
                        <input type="date" value={txn.date || ''} onChange={(e) => updateTransaction(idx, 'date', e.target.value)}
                          className="bg-transparent border-0 text-sm text-foreground w-32 focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded px-1" />
                      </td>
                      <td className="p-3">
                        <input type="text" value={txn.description || ''} onChange={(e) => updateTransaction(idx, 'description', e.target.value)}
                          className="bg-transparent border-0 text-sm text-foreground w-full focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded px-1" />
                      </td>
                      <td className="p-3 text-right">
                        <span className={cn("font-mono font-medium text-sm",
                          txn.type === 'credit' ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                        )}>
                          {txn.type === 'credit' ? '+' : ''}{typeof txn.amount === 'number' ? txn.amount.toFixed(2) : txn.amount}
                        </span>
                      </td>
                      <td className="p-3">
                        <select value={txn.type || 'debit'} onChange={(e) => updateTransaction(idx, 'type', e.target.value)}
                          className="bg-transparent border-0 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded">
                          <option value="credit">Credit</option>
                          <option value="debit">Debit</option>
                        </select>
                      </td>
                      <td className="p-3">
                        <select value={txn.category || 'Other'} onChange={(e) => updateTransaction(idx, 'category', e.target.value)}
                          className="bg-transparent border-0 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded">
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isProcessing && (
              <div className="p-4 border-t border-border flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400" data-testid="loading-more-indicator">
                <Loader2 className="w-4 h-4 animate-spin" />
                Extracting remaining pages in background... New transactions will appear automatically.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── INVOICE RESULTS ──────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="scanner-invoice-results">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <p className="font-semibold text-foreground">Invoice Extracted</p>
              {isProcessing && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1 animate-pulse">
                  <Loader2 className="w-3 h-3 animate-spin" /> Processing more pages...
                </Badge>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={handleReset}>
              <ScanLine className="w-3 h-3 mr-1" /> Scan Another
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Vendor</p>
              <p className="font-medium text-foreground">{job.vendor_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Invoice #</p>
              <p className="font-medium text-foreground">{job.invoice_number || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="font-medium text-foreground">{job.invoice_date || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="font-mono font-bold text-lg text-emerald-600 dark:text-emerald-400">
                ${typeof job.total === 'number' ? job.total.toFixed(2) : job.total || '—'}
              </p>
            </div>
          </div>

          {(job.line_items || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted">
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Description</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Qty</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Unit Price</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(job.line_items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-border">
                      <td className="p-3 text-foreground">{item.description}</td>
                      <td className="p-3 text-right font-mono text-muted-foreground">{item.quantity}</td>
                      <td className="p-3 text-right font-mono text-muted-foreground">
                        ${typeof item.unit_price === 'number' ? item.unit_price.toFixed(2) : item.unit_price}
                      </td>
                      <td className="p-3 text-right font-mono font-medium text-foreground">
                        ${typeof item.total === 'number' ? item.total.toFixed(2) : item.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-end gap-4 text-sm pt-2 border-t border-border">
            <div>Subtotal: <span className="font-mono">${typeof job.subtotal === 'number' ? job.subtotal.toFixed(2) : job.subtotal || '—'}</span></div>
            <div>Tax: <span className="font-mono">${typeof job.tax === 'number' ? job.tax.toFixed(2) : job.tax || '—'}</span></div>
            <div className="font-bold">Total: <span className="font-mono text-emerald-600">${typeof job.total === 'number' ? job.total.toFixed(2) : job.total || '—'}</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
